import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Divider, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { removeItemFromUpdateQueue } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import CustomPropertySecProcess from '@/features/secondary-processes/CustomPropertySecProcess'
import QuantitySecProcess from '@/features/secondary-processes/QuantitySecProcess'

const SecondaryProcesses = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [itemSecondaryProcesses, setItemSecondaryProcesses] = useState([])

    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const [updateOrderItem] = useUpdateOrderItemMutation()

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const onQuantityInputChange = async (process) => {
        const processIds = itemSecondaryProcesses?.map((sp) => sp.secondaryProcessId)
        const processIndex = processIds.indexOf(process.secondaryProcessId)
        const newProcessList = [...itemSecondaryProcesses]
        newProcessList[processIndex] = process
        setItemSecondaryProcesses(newProcessList)

        await handleUpdateOrderItem({ ...orderItem, secondaryProcesses: newProcessList })
        dispatch(removeItemFromUpdateQueue(orderItemId))
    }

    const getProcessDetails = (processId) =>
        organisationSecondaryProcesses?.find((osp) => osp.secondaryProcessId === processId)

    const sortedProcesses = useMemo(() => {
        return itemSecondaryProcesses
            ?.map((process) => ({
                ...process,
                index: getProcessDetails(process.secondaryProcessId)?.index,
                customPropertyName: getProcessDetails(process.secondaryProcessId)?.customPropertyName,
                customPropertyOptions: getProcessDetails(process.secondaryProcessId)?.customPropertyOptions,
                customValue: process.customValue,
            }))
            .sort((a, b) => (a.index < b.index ? -1 : 1))
    }, [itemSecondaryProcesses])

    useEffect(() => {
        setItemSecondaryProcesses(orderItem?.secondaryProcesses)
    }, [orderItem?.secondaryProcesses])

    return sortedProcesses?.map((process, index) => (
        <>
            {index !== 0 ? <Divider sx={{ opacity: 0.1, marginBlock: 1.5 }} /> : null}
            <Box
                key={process.secondaryProcessId}
                sx={{
                    padding: 1,
                }}
            >
                <Typography
                    data-testid="order-item-secondary-process-label"
                    variant="body2"
                >
                    {getProcessDetails(process.secondaryProcessId)?.name}
                </Typography>

                {getProcessDetails(process.secondaryProcessId)?.customPropertyName ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            justifyContent: 'space-between',
                            marginTop: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <Typography
                                color="text.secondary"
                                variant="small"
                            >
                                {t(process.customPropertyName)}
                            </Typography>
                        </Box>

                        <CustomPropertySecProcess
                            isPartFromLibrary={isPartFromLibrary}
                            orderItemId={orderItemId}
                            process={process}
                            showLabel={false}
                            sx={{
                                flex: 1,
                            }}
                            onInputChange={onQuantityInputChange}
                        />
                    </Box>
                ) : null}

                {getProcessDetails(process.secondaryProcessId)?.measureType === 'Quantity' ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            justifyContent: 'space-between',
                            marginTop: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <Typography
                                color="text.secondary"
                                variant="small"
                            >
                                {t('Quantity')}
                            </Typography>
                        </Box>

                        <QuantitySecProcess
                            isPartFromLibrary={isPartFromLibrary}
                            orderItemId={orderItemId}
                            process={process}
                            showLabel={false}
                            sx={{
                                flex: 1,
                            }}
                            onInputChange={onQuantityInputChange}
                        />
                    </Box>
                ) : null}
            </Box>
        </>
    ))
}

SecondaryProcesses.propTypes = {
    orderItemId: PropTypes.string,
}

export default SecondaryProcesses
